import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, History, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface CleanupLog {
  id: string;
  user_email: string;
  deleted_at: string;
  cleanup_type: string;
  deleted_by: string | null;
  metadata: any;
}

export const OrphanedUserCleanupHistory = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<CleanupLog[]>([]);
  const [filter, setFilter] = useState<'all' | 'auto' | 'manual'>('all');
  const { toast } = useToast();

  const loadCleanupHistory = async () => {
    setLoading(true);
    try {
      // Use raw SQL query since table is not in types yet
      const { data, error } = await supabase
        .from('orphaned_user_cleanup_log' as any)
        .select('*')
        .order('deleted_at', { ascending: false })
        .limit(50) as any;

      if (error) throw error;

      setLogs(data || []);
    } catch (error: any) {
      console.error('Load history error:', error);
      toast({
        title: "Failed to Load History",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCleanupHistory();
  }, [filter]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Cleanup History
        </CardTitle>
        <CardDescription>
          View history of orphaned user cleanup operations
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={filter} onValueChange={(value: any) => setFilter(value)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="auto">Automatic</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No cleanup history found</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {logs.map((log) => (
              <div 
                key={log.id}
                className="flex items-center justify-between p-3 border rounded-lg bg-card hover:bg-muted/50 transition-colors"
              >
                <div className="flex-1">
                  <p className="font-medium">{log.user_email}</p>
                  <p className="text-xs text-muted-foreground">
                    Deleted: {new Date(log.deleted_at).toLocaleString()}
                  </p>
                  {log.metadata?.has_related_data && (
                    <p className="text-xs text-amber-600 mt-1">
                      Had related data
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={log.cleanup_type === 'auto' ? 'default' : 'secondary'}>
                    {log.cleanup_type === 'auto' ? 'Automatic' : 'Manual'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {logs.length > 0 && (
          <p className="text-xs text-muted-foreground text-center">
            Showing {logs.length} most recent cleanup operations
          </p>
        )}
      </CardContent>
    </Card>
  );
};
